import React from 'react';

import PropTypes from 'prop-types';

import { VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { StylableComponent } from '@adsk/alloy-react-theme';

import { DATE_PICKER_VARIANTS } from './consts';
import { defaultRenderTrigger } from './helpers/defaultRenderers';
import useDatePicker from './hooks/useDatePicker';
import Footer from './layout/Footer';
import DatePickerOverlay from './layout/Overlay';
import PickerOverlayPortal from './layout/PickerOverlayPortal';
import Trigger from './layout/Trigger';
import { datePickerCommonPropTypes } from './propTypes/datePickerCommonPropTypes';
import type { DatePickerArgs, DatePickerCommonArgs } from './types';

type DatePickerProps = DatePickerCommonArgs &
  DatePickerArgs &
  Omit<StylableComponent<HTMLDivElement>, 'onChange'>;
/**
 * The Date Picker is a basic component that allows the user to pick a single date.
 * Date pickers allow the selection of specific dates and years. It displays a default
 * text field and a dropdown calendar appears when a user taps on the text field.
 */
const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      style,
      className,
      variant = DATE_PICKER_VARIANTS.FIELD,
      container = document.body,
      renderTrigger = defaultRenderTrigger,
      overlayProps = {},
      initialValueSelected = true,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;

    const target = useForwardedRef(ref);
    const [shouldShowSelection, setShouldShowSelection] =
      React.useState<boolean>(initialValueSelected);
    const datePickerData = useDatePicker({
      variant,
      target,
      setShouldShowSelection,
      ...props,
    });
    const {
      context,
      open,
      targetProps,
      placement,
      getTriggerProps,
      arrowProps,
      overlayContainerProps,
    } = datePickerData;

    return (
      <PickerOverlayPortal
        className={className}
        style={style}
        container={container}
        open={open}
        context={context}
        targetProps={targetProps}
        placement={placement}
        getTriggerProps={getTriggerProps}
        arrowProps={arrowProps}
        overlayContainerProps={overlayContainerProps}
        arrowColor={t?.arrow?.backgroundColor}
        variant={variant}
        renderTrigger={renderTrigger}
        renderOverlay={() => (
          <DatePickerOverlay
            double={props.double}
            timeZone={props.timeZone}
            calendarProps={props.calendarProps}
            onMonthChange={props.onMonthChange}
            showOutsideDays={props.showOutsideDays}
            isDateSelectable={props.isDateSelectable}
            yearsToDisplay={props.yearsToDisplay}
            firstDayOfWeek={props.firstDayOfWeek}
            tooltipContentForDate={props.tooltipContentForDate}
            hasEventForDate={props.hasEventForDate}
            renderFooter={props.renderFooter}
            renderFooterActions={props.renderFooterActions}
            minDate={datePickerData.minDate}
            shouldShowSelection={shouldShowSelection}
            maxDate={datePickerData.maxDate}
            value={datePickerData.date}
            startDate={null}
            endDate={null}
            toggleOverlay={datePickerData.toggleOverlay}
            onDateSelected={datePickerData.onDateSelected}
            resetSelection={datePickerData.resetSelection}
            {...overlayProps}
          />
        )}
      />
    );
  },
);

DatePicker.displayName = 'DatePicker';

DatePicker.propTypes = {
  ...datePickerCommonPropTypes,
  /** A selected date for a calendar */
  value: PropTypes.instanceOf(Date),
  /** The initial date for calendar */
  initialValue: PropTypes.instanceOf(Date),
  /** Passed to the field, placeholder to display when there is no value */
  placeholder: PropTypes.string,
  /** Render custom footer, executed in DatePicker */
  renderTrigger: PropTypes.func,
};

export default Object.assign(DatePicker, {
  VARIANTS: DATE_PICKER_VARIANTS,
  VALID_FORMATS: VALID_DATE_FORMATS,
  Footer,
  Trigger,
});
